import { memo, useMemo } from "react";
import { Theme } from "@mui/material";
import Grid from "components/primitives/Grid";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import type { Athlete } from "types/athlete";
import Mui5 from "components/mui5";
import AthleteHelpers from "helpers/AthleteHelpers";
import useAthleteProfilePhotoURL from "hooks/useAthleteProfilePhotoURL";
import { useTranslation } from "react-i18next";
import useSearchText from "providers/SearchTextProvider/useSearchText";
import TextHighlight from "components/TextHighlight";

function NameHighlight(props: { name: string }) {
  const searchText = useSearchText();

  return (
    <Typography
      data-sensitive
      color="textPrimary"
      variant="h4"
      noWrap
      title={props.name}
      sx={{ pr: 1, fontWeight: 500, textTransform: "none" }}
    >
      <TextHighlight text={props.name} searchText={searchText} />
    </Typography>
  );
}

interface AthleteNameProps {
  athlete: Athlete | undefined;
  avatarColor?: string;
  showGroups?: boolean;
  isSearchable?: boolean;
}

/**
 * @description Displays the athlete's name and avatar.
 * If the athlete is deleted, it shows a placeholder name.
 */
function AthleteName(props: AthleteNameProps) {
  const { athlete, avatarColor, isSearchable = false } = props;

  const { t } = useTranslation();
  const avatar = useAthleteProfilePhotoURL(athlete);

  const fullName = useMemo(
    () => (athlete ? AthleteHelpers.getFullName(athlete) : `[${t("global.deleted_athlete_label")}]`),
    [athlete, t]
  );
  const avatarCustomColor = useMemo(
    () =>
      avatarColor
        ? (theme: Theme) => ({
            backgroundColor: avatarColor,
            color: theme.palette.getContrastText(avatarColor),
          })
        : undefined,
    [avatarColor]
  );

  return (
    <Grid
      container
      alignItems="center"
      p={0}
      spacing="0px"
      flexWrap="nowrap"
      sx={{
        width: "100%",
      }}
    >
      <Grid>
        <Mui5.Avatar sx={avatarCustomColor} src={avatar.src} alt={avatar.alt}>
          {avatar.initials}
        </Mui5.Avatar>
      </Grid>
      <Grid size="grow">
        <Grid
          container
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <Grid size="grow">
            <Box sx={{ ml: 2, minWidth: 0 }}>
              {isSearchable ? (
                <NameHighlight name={fullName} />
              ) : (
                <Typography
                  data-sensitive
                  color="textPrimary"
                  variant="h4"
                  noWrap
                  title={fullName}
                  sx={{ pr: 1, fontWeight: 500, textTransform: "none" }}
                >
                  {fullName}
                </Typography>
              )}
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
}
export default memo(AthleteName);
